import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SecurityIcon from '@mui/icons-material/Security';
import TvIcon from '@mui/icons-material/Tv';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DescriptionIcon from '@mui/icons-material/Description';

import { useProjects } from '../hooks/useContent';
import { ProjectStatus, ProjectActionType } from '../types/content';

const Projects: React.FC = () => {
  const { projects } = useProjects();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProjectStatus>('all');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [expandedSlug, setExpandedSlug] = useState<string | null>('track-me');

  // Extract all unique tags
  const allTags = useMemo(() => {
    const set = new Set<string>();
    projects.forEach((p) => p.tags.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [projects]);

  // Filtered projects
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const matchesSearch =
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.tagline && p.tagline.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        p.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesTag = !selectedTag || p.tags.includes(selectedTag);

      return matchesSearch && matchesStatus && matchesTag;
    });
  }, [projects, searchQuery, statusFilter, selectedTag]);

  const toggleExpand = (slug: string) => {
    setExpandedSlug((prev) => (prev === slug ? null : slug));
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return { text: '#38BDF8', bg: 'rgba(56, 189, 248, 0.1)', border: 'rgba(56, 189, 248, 0.3)' };
      case 'shipped':
        return { text: '#4ADE80', bg: 'rgba(74, 222, 128, 0.1)', border: 'rgba(74, 222, 128, 0.3)' };
      case 'idea':
        return { text: '#FACC15', bg: 'rgba(250, 204, 21, 0.1)', border: 'rgba(250, 204, 21, 0.3)' };
      case 'archived':
        return { text: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.3)' };
      default:
        return { text: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.3)' };
    }
  };

  const getActionStyle = (type?: ProjectActionType) => {
    switch (type) {
      case 'landing':
      case 'live':
        return {
          color: '#38BDF8',
          borderColor: 'rgba(56, 189, 248, 0.35)',
          hoverBg: 'rgba(56, 189, 248, 0.1)',
          icon: <LaunchIcon sx={{ fontSize: 14 }} />,
        };
      case 'auth':
        return {
          color: '#EFC430',
          borderColor: 'rgba(239, 196, 48, 0.35)',
          hoverBg: 'rgba(239, 196, 48, 0.1)',
          icon: <SecurityIcon sx={{ fontSize: 14 }} />,
        };
      case 'media':
        return {
          color: '#C084FC',
          borderColor: 'rgba(192, 132, 252, 0.35)',
          hoverBg: 'rgba(192, 132, 252, 0.1)',
          icon: <TvIcon sx={{ fontSize: 14 }} />,
        };
      case 'cloud':
        return {
          color: '#34D399',
          borderColor: 'rgba(52, 211, 153, 0.35)',
          hoverBg: 'rgba(52, 211, 153, 0.1)',
          icon: <CloudQueueIcon sx={{ fontSize: 14 }} />,
        };
      case 'docs':
        return {
          color: '#FBBF24',
          borderColor: 'rgba(251, 191, 36, 0.35)',
          hoverBg: 'rgba(251, 191, 36, 0.1)',
          icon: <DescriptionIcon sx={{ fontSize: 14 }} />,
        };
      case 'code':
        return {
          color: '#E5E7EB',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          hoverBg: 'rgba(255, 255, 255, 0.08)',
          icon: <GitHubIcon sx={{ fontSize: 14 }} />,
        };
      default:
        return {
          color: '#E5E7EB',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          hoverBg: 'rgba(255, 255, 255, 0.08)',
          icon: <LaunchIcon sx={{ fontSize: 14 }} />,
        };
    }
  };

  return (
    <main
      style={{
        position: 'relative',
        zIndex: 1,
        minHeight: 'calc(100vh - 64px)',
        paddingTop: 48,
        paddingBottom: 80,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Page Title & Intro */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ marginBottom: 36 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.5rem' }}>🔨</span>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '2.4rem' },
                background: 'linear-gradient(135deg, #EFC430 0%, #F4D675 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Projects & Vibe Experiments
            </Typography>
          </div>

          <Typography variant="body1" sx={{ color: '#9CA3AF', mt: 1, maxWidth: 680, lineHeight: 1.6 }}>
            Independent projects built outside work — personal safety telemetry, 24/7 low-power homelab,
            Android Health Connect tracking, and cloud data plane architectures.
          </Typography>
        </motion.div>

        {/* Search & Filter Controls */}
        <div
          style={{
            backgroundColor: 'rgba(20, 20, 22, 0.65)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 18,
            padding: '20px 24px',
            marginBottom: 32,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Top row: Search input + Status buttons */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <TextField
              size="small"
              placeholder="Search projects or technologies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#EFC430', fontSize: 20 }} />
                  </InputAdornment>
                ),
                style: {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 12,
                  fontSize: '0.88rem',
                },
              }}
              sx={{
                minWidth: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            />

            {/* Status filters */}
            <div
              style={{
                display: 'flex',
                gap: 6,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                padding: 4,
                borderRadius: 12,
                border: '1px solid rgba(255, 255, 255, 0.06)',
                flexWrap: 'wrap',
              }}
            >
              {(
                [
                  { key: 'all', label: 'All' },
                  { key: 'active', label: 'Active Beta' },
                  { key: 'shipped', label: 'Shipped & 24/7' },
                  { key: 'idea', label: 'In Dev / Ideas' },
                ] as const
              ).map((item) => (
                <Button
                  key={item.key}
                  size="small"
                  onClick={() => setStatusFilter(item.key)}
                  sx={{
                    color: statusFilter === item.key ? '#0A0A0A' : '#D1D5DB',
                    backgroundColor: statusFilter === item.key ? '#EFC430' : 'transparent',
                    fontWeight: statusFilter === item.key ? 700 : 500,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    px: 1.4,
                    py: 0.4,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: statusFilter === item.key ? '#EFC430' : 'rgba(255, 255, 255, 0.06)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Tag Cloud Pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
            <span style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 600 }}>Filter by Tech:</span>
            {selectedTag && (
              <Chip
                label="Clear Filter"
                size="small"
                onDelete={() => setSelectedTag(null)}
                sx={{
                  backgroundColor: 'rgba(239, 196, 48, 0.2)',
                  color: '#EFC430',
                  fontSize: '0.72rem',
                  height: 22,
                }}
              />
            )}
            {allTags.slice(0, 10).map((tag) => {
              const isSelected = selectedTag === tag;
              return (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onClick={() => setSelectedTag(isSelected ? null : tag)}
                  sx={{
                    backgroundColor: isSelected ? '#EFC430' : 'rgba(255, 255, 255, 0.04)',
                    color: isSelected ? '#0A0A0A' : '#9CA3AF',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    fontSize: '0.72rem',
                    height: 22,
                    cursor: 'pointer',
                    fontWeight: isSelected ? 700 : 500,
                    '&:hover': {
                      borderColor: '#EFC430',
                      color: isSelected ? '#0A0A0A' : '#FFFFFF',
                    },
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Projects Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 22,
          }}
        >
          {filteredProjects.map((project) => {
            const isExpanded = expandedSlug === project.slug;
            const statusStyle = getStatusColor(project.status);

            return (
              <article
                key={project.slug}
                style={{
                  borderRadius: 20,
                  backgroundColor: 'rgba(20, 20, 22, 0.65)',
                  backdropFilter: 'blur(16px)',
                  border: isExpanded
                    ? '1px solid rgba(239, 196, 48, 0.35)'
                    : '1px solid rgba(255, 255, 255, 0.08)',
                  padding: '26px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: isExpanded
                    ? '0 12px 36px rgba(239, 196, 48, 0.08)'
                    : '0 4px 24px rgba(0, 0, 0, 0.25)',
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              >
                <div>
                  {/* Category & Status */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {project.tags[0] || 'Project'}
                    </span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {project.pinned && (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            fontSize: '0.7rem',
                            color: '#EFC430',
                            backgroundColor: 'rgba(239, 196, 48, 0.1)',
                            padding: '2px 8px',
                            borderRadius: 8,
                            fontWeight: 600,
                          }}
                        >
                          <StarOutlineIcon sx={{ fontSize: 13 }} />
                          Featured
                        </span>
                      )}

                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: statusStyle.text,
                          backgroundColor: statusStyle.bg,
                          border: `1px solid ${statusStyle.border}`,
                          padding: '2px 8px',
                          borderRadius: 8,
                          textTransform: 'uppercase',
                        }}
                      >
                        {project.status}
                      </span>
                    </div>
                  </div>

                  {/* Title & Tagline */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      gap: 8,
                    }}
                    onClick={() => toggleExpand(project.slug)}
                  >
                    <div>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                          lineHeight: 1.3,
                          mb: 0.5,
                        }}
                      >
                        {project.title}
                      </Typography>

                      {project.tagline && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#EFC430',
                            fontSize: '0.86rem',
                            fontWeight: 500,
                            mb: 1.8,
                            lineHeight: 1.4,
                          }}
                        >
                          {project.tagline}
                        </Typography>
                      )}
                    </div>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(project.slug);
                      }}
                      sx={{
                        color: '#EFC430',
                        backgroundColor: 'rgba(239, 196, 48, 0.08)',
                        '&:hover': { backgroundColor: 'rgba(239, 196, 48, 0.18)' },
                      }}
                      aria-label={isExpanded ? 'Collapse project details' : 'Expand project details'}
                    >
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </div>

                  {/* Description preview */}
                  {project.description && (
                    <Collapse in={isExpanded} collapsedSize={60}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#9CA3AF',
                          fontSize: '0.88rem',
                          lineHeight: 1.6,
                          mb: 2,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {project.description}
                      </Typography>
                    </Collapse>
                  )}

                  {/* Tech stack chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18, marginTop: 8 }}>
                    {project.tags.map((tech) => (
                      <Chip
                        key={tech}
                        label={tech}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          color: '#D1D5DB',
                          fontSize: '0.72rem',
                          height: 22,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Footer Action Buttons */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 14,
                    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {project.links.actions && project.links.actions.length > 0
                      ? project.links.actions.map((act) => {
                          const style = getActionStyle(act.type);
                          return (
                            <Button
                              key={act.url}
                              size="small"
                              variant="outlined"
                              startIcon={style.icon}
                              onClick={() => window.open(act.url, '_blank')}
                              sx={{
                                color: style.color,
                                borderColor: style.borderColor,
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                borderRadius: '8px',
                                px: 1.2,
                                py: 0.4,
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: style.hoverBg,
                                  borderColor: style.color,
                                },
                              }}
                            >
                              {act.label}
                            </Button>
                          );
                        })
                      : null}

                    {/* Fallback code button */}
                    {project.links.github &&
                      (!project.links.actions || !project.links.actions.some((a) => a.url === project.links.github)) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<GitHubIcon sx={{ fontSize: 14 }} />}
                          onClick={() => window.open(project.links.github, '_blank')}
                          sx={{
                            color: '#E5E7EB',
                            borderColor: 'rgba(255, 255, 255, 0.15)',
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            borderRadius: '8px',
                            '&:hover': { borderColor: '#EFC430', backgroundColor: 'rgba(239, 196, 48, 0.08)' },
                          }}
                        >
                          Code
                        </Button>
                      )}

                    {/* Fallback live button */}
                    {project.links.live &&
                      (!project.links.actions || !project.links.actions.some((a) => a.url === project.links.live)) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                          onClick={() => window.open(project.links.live, '_blank')}
                          sx={{
                            color: '#38BDF8',
                            borderColor: 'rgba(56, 189, 248, 0.3)',
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: 'rgba(56, 189, 248, 0.08)' },
                          }}
                        >
                          Live
                        </Button>
                      )}

                    {/* Fallback article button */}
                    {project.links.article &&
                      (!project.links.actions || !project.links.actions.some((a) => a.url === project.links.article)) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ArticleIcon sx={{ fontSize: 14 }} />}
                          onClick={() => window.open(project.links.article, '_blank')}
                          sx={{
                            color: '#EFC430',
                            borderColor: 'rgba(239, 196, 48, 0.3)',
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: 'rgba(239, 196, 48, 0.08)' },
                          }}
                        >
                          Article
                        </Button>
                      )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </Container>
    </main>
  );
};

export default Projects;
